import { AppContent, NavigationConfig, FormStep } from '../types';
import { appContent, navigationConfig, formSteps } from '../mocks/content';

// Interface seguindo o princípio da Inversão de Dependência (SOLID)
export interface IContentService {
  getAppContent(): Promise<AppContent>;
  getNavigationConfig(): Promise<NavigationConfig>;
  getFormSteps(): Promise<FormStep[]>;
  getHeroContent(): Promise<AppContent['hero']>;
  getFAQItems(): Promise<AppContent['faq']>;
  getMetrics(): Promise<AppContent['metrics']>;
  clearCache?(): void;
}

// Implementação concreta do serviço
export class ContentService implements IContentService {
  private static instance: ContentService;
  private contentCache: AppContent | null = null;
  private navigationCache: NavigationConfig | null = null;
  private formStepsCache: FormStep[] | null = null;

  // Singleton pattern para garantir uma única instância
  public static getInstance(): ContentService {
    if (!ContentService.instance) {
      ContentService.instance = new ContentService();
    }
    return ContentService.instance;
  }

  private constructor() {}

  async getAppContent(): Promise<AppContent> {
    if (!this.contentCache) {
      // Simula uma chamada assíncrona (poderia ser uma API)
      await this.delay(100);
      this.contentCache = appContent;
    }
    return this.contentCache;
  }

  async getNavigationConfig(): Promise<NavigationConfig> {
    if (!this.navigationCache) {
      await this.delay(50);
      this.navigationCache = navigationConfig;
    }
    return this.navigationCache;
  }

  async getFormSteps(): Promise<FormStep[]> {
    if (!this.formStepsCache) {
      await this.delay(75);
      this.formStepsCache = formSteps;
    }
    return this.formStepsCache;
  }

  async getHeroContent(): Promise<AppContent['hero']> {
    const content = await this.getAppContent();
    return content.hero;
  }

  async getFAQItems(): Promise<AppContent['faq']> {
    const content = await this.getAppContent();
    return content.faq;
  }

  async getMetrics(): Promise<AppContent['metrics']> {
    const content = await this.getAppContent();
    return content.metrics;
  }

  // Método utilitário para simular delay de rede
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // Método para limpar cache (útil para testes)
  public clearCache(): void {
    this.contentCache = null;
    this.navigationCache = null;
    this.formStepsCache = null;
  }
}

// Factory function para facilitar a injeção de dependência
export const createContentService = (): IContentService => {
  return ContentService.getInstance();
};